import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
  CircularProgress,
  Box
} from '@material-ui/core'
import { ATTACHMENT_TYPES } from '../utils/attachmentUtils'
import { useAttachment } from '../contexts/AttachmentContext'
import { useAIThreadAttachment } from '../../../../api/denaliAssistant'
import Alert from '../../../../components/atoms/Alert'
import OpalHeading from '../../../../components/opal/OpalHeading'
import SydButton from '../../../../components/commonDesign/Button'
import { AttachmentPanel } from './AttachmentPanel'

const QueryResultsViewer = lazy(() => import('./viewers/QueryResultsViewer'))
const JSXViewer = lazy(() => import('./viewers/JSXViewer'))
const DQLViewer = lazy(() => import('./viewers/DQLViewer'))
const CSVViewer = lazy(() => import('./viewers/CSVViewer'))
const EmailViewer = lazy(() => import('./viewers/EmailViewer'))
const MarkdownViewer = lazy(() => import('./viewers/MarkdownViewer'))
const ToolCallViewer = lazy(() => import('./viewers/ToolCallViewer'))
const DefaultViewer = lazy(() => import('./viewers/DefaultViewer'))

const useStyles = makeStyles((theme) => ({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
    padding: theme.spacing(4)
  },
  errorContainer: {
    padding: theme.spacing(3)
  },
  viewerContainer: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative'
  }
}))

const VIEW_MODE_CONFIGS = {
  jsx: { modes: [{ key: 'preview', icon: 'visibility', label: 'Preview' }, { key: 'source', icon: 'code', label: 'Source' }] },
  dql: { modes: [{ key: 'query', icon: 'database', label: 'Query View' }, { key: 'details', icon: 'document', label: 'Details View' }] },
  catalog: { modes: [{ key: 'catalog', icon: 'viewListConfig', label: 'Catalog View' }, { key: 'details', icon: 'document', label: 'Details View' }] },
  plan: { modes: [{ key: 'plan', icon: 'list', label: 'Plan View' }, { key: 'details', icon: 'document', label: 'Details View' }] },
  run: { modes: [{ key: 'code', icon: 'code', label: 'Code View' }, { key: 'details', icon: 'document', label: 'Details View' }] },
  files: { modes: [{ key: 'files', icon: 'folder', label: 'Files View' }, { key: 'details', icon: 'document', label: 'Details View' }] },
  export: { modes: [{ key: 'export', icon: 'download', label: 'Export View' }, { key: 'details', icon: 'document', label: 'Details View' }] },
  api: { modes: [{ key: 'api', icon: 'link', label: 'API View' }, { key: 'details', icon: 'document', label: 'Details View' }] },
  visualize: { modes: [{ key: 'visualize', icon: 'chart', label: 'Visualization View' }, { key: 'details', icon: 'document', label: 'Details View' }] },
  actions: { modes: [{ key: 'actions', icon: 'list', label: 'Actions View' }, { key: 'details', icon: 'document', label: 'Details View' }] }
}

function getViewModeConfig (attachment) {
  if (!attachment) return null
  const { contentType, data } = attachment
  if (contentType === ATTACHMENT_TYPES.JSX_CODE) return VIEW_MODE_CONFIGS.jsx
  if (contentType !== ATTACHMENT_TYPES.TOOL_CALL) return null
  const subType = data?.subType
  if (subType === 'execute_dql_query' || subType === 'query') return VIEW_MODE_CONFIGS.dql
  if (subType === 'get_catalog_schema' || subType === 'catalog') return VIEW_MODE_CONFIGS.catalog
  if (subType === 'plan') return VIEW_MODE_CONFIGS.plan
  if (subType === 'run') return VIEW_MODE_CONFIGS.run
  if (subType === 'files') return VIEW_MODE_CONFIGS.files
  if (subType === 'export') return VIEW_MODE_CONFIGS.export
  if (subType === 'api') return VIEW_MODE_CONFIGS.api
  if (subType === 'visualize') return VIEW_MODE_CONFIGS.visualize
  if (subType === 'actions') return VIEW_MODE_CONFIGS.actions
  return null
}

function getDefaultViewMode (attachment) {
  if (attachment?.contentType === ATTACHMENT_TYPES.TOOL_CALL) {
    const subType = attachment?.data?.subType
    const map = {
      get_catalog_schema: 'catalog',
      catalog: 'catalog',
      execute_dql_query: 'query',
      query: 'query',
      plan: 'plan',
      run: 'code',
      files: 'files',
      export: 'export',
      api: 'api',
      visualize: 'visualize',
      actions: 'actions'
    }
    return map[subType] || 'details'
  }
  return 'preview'
}

export function AttachmentViewer () {
  const classes = useStyles()
  const { selectedAttachment, isOpen, closeAttachment, focus } = useAttachment()
  const [viewMode, setViewMode] = useState('preview')
  const [viewerActions, setViewerActions] = useState(null)

  const isInlineAttachment = selectedAttachment?.inline && selectedAttachment?.data
  const needsRemoteFetch = !selectedAttachment?.inline && !!selectedAttachment?.threadId && !!selectedAttachment?.attachmentId

  const { data: remoteData, error: fetchError, isLoading } = useAIThreadAttachment(
    selectedAttachment?.threadId,
    selectedAttachment?.attachmentId,
    { enabled: isOpen && needsRemoteFetch }
  )

  useEffect(() => {
    setViewMode(getDefaultViewMode(selectedAttachment))
  }, [selectedAttachment])

  const attachmentData = isInlineAttachment
    ? selectedAttachment.data
    : remoteData?.attachment?.data || remoteData?.data || remoteData

  const error = fetchError?.message ||
    (selectedAttachment?.inline && !selectedAttachment?.data ? 'Attachment data is not available' : null)

  const onRegisterActions = useCallback((actions) => {
    setViewerActions(actions)
  }, [])

  const getViewer = () => {
    if (needsRemoteFetch && isLoading) {
      return (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      )
    }

    if (error) {
      return (
        <Box className={classes.errorContainer}>
          <Alert severity='error'>
            <OpalHeading>Unable to load attachment</OpalHeading>
            {error}
          </Alert>
        </Box>
      )
    }

    if (!attachmentData) return null

    const viewerProps = {
      data: attachmentData,
      metadata: selectedAttachment,
      onRegisterActions
    }

    const isQueryResultsArtifact = selectedAttachment?.contentType === ATTACHMENT_TYPES.ARTIFACT_QUERY_RESULTS ||
                                   selectedAttachment?.metadata?.artifactType === 'query_results' ||
                                   selectedAttachment?.artifactType === 'query_results'

    if (isQueryResultsArtifact) {
      return <QueryResultsViewer {...viewerProps} />
    }

    switch (selectedAttachment?.contentType) {
      case ATTACHMENT_TYPES.QUERY_RESULTS:
      case ATTACHMENT_TYPES.ARTIFACT_QUERY_RESULTS:
        return <QueryResultsViewer {...viewerProps} />

      case ATTACHMENT_TYPES.JSX_CODE:
        return (
          <JSXViewer
            {...viewerProps}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        )

      case ATTACHMENT_TYPES.DQL_QUERY:
      case ATTACHMENT_TYPES.SQL_QUERY:
        return <DQLViewer {...viewerProps} />

      case ATTACHMENT_TYPES.CSV_DATA:
        return <CSVViewer {...viewerProps} />

      case ATTACHMENT_TYPES.ARTIFACT_EMAIL:
        return <EmailViewer {...viewerProps} />

      case ATTACHMENT_TYPES.ARTIFACT_AGENDA:
      case ATTACHMENT_TYPES.ARTIFACT_TALKING_POINTS:
      case ATTACHMENT_TYPES.ARTIFACT_PROPOSAL:
      case ATTACHMENT_TYPES.ARTIFACT_SUMMARY:
        return <MarkdownViewer {...viewerProps} />

      case ATTACHMENT_TYPES.TOOL_CALL:
        return (
          <ToolCallViewer
            {...viewerProps}
            viewMode={viewMode}
            focus={focus}
          />
        )

      default:
        return <DefaultViewer {...viewerProps} />
    }
  }

  const viewModeConfig = getViewModeConfig(selectedAttachment)

  const viewToggleActions = viewModeConfig ? (
    <>
      {viewModeConfig.modes.map(({ key, icon, label }) => (
        <SydButton
          key={key}
          size='sm'
          variant={viewMode === key ? 'primary' : 'ghost'}
          onClick={() => setViewMode(key)}
          icon={icon}
        >
          {label}
        </SydButton>
      ))}
    </>
  ) : null

  const canPopout = !!(selectedAttachment?.threadId && selectedAttachment?.attachmentId)

  const handlePopout = useCallback(() => {
    const tid = selectedAttachment?.threadId
    const aid = selectedAttachment?.attachmentId
    if (tid && aid) {
      window.open(`/denali-ai/artifact/${tid}/${aid}`, '_blank', 'noopener')
    }
  }, [selectedAttachment?.threadId, selectedAttachment?.attachmentId])

  const combinedActions = (viewToggleActions || viewerActions) ? (
    <>
      {viewToggleActions}
      {viewerActions}
    </>
  ) : null

  return (
    <AttachmentPanel
      open={isOpen}
      onClose={closeAttachment}
      title={selectedAttachment?.title || 'Attachment'}
      viewerActions={combinedActions}
      onPopout={canPopout ? handlePopout : null}
    >
      <div className={classes.viewerContainer} key={selectedAttachment?.attachmentId}>
        <Suspense fallback={
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        }
        >
          {getViewer()}
        </Suspense>
      </div>
    </AttachmentPanel>
  )
}
